<script setup lang="ts">
/**
 * Live artifact preview (B7) — a lightweight, in-module rendering of the resource
 * draft the editor agent is mutating. It pins beside (split) or within (inline)
 * the chat takeover and re-renders each time a `resource_*` tool changes the
 * server-held draft (the latest snapshot is streamed on a `resource` part and
 * kept as transient state in `useChat`).
 *
 * This is a structural outline only — block id, type and heading, indented by
 * depth — deliberately schema-agnostic so it works for any resource definition
 * (report or assessment) without product-specific knowledge. Hosts that want a
 * full building-block render can override the takeover `#resource` slot.
 */
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Badge } from "@/components/ui/badge";
import type { ResourceArtifact } from "../../types/chat";

const props = withDefaults(
  defineProps<{
    artifact: ResourceArtifact;
    /** Rendered in the narrow/inline collapsed pane vs the wide split pane. */
    compact?: boolean;
  }>(),
  { compact: false },
);

const { t } = useI18n({ useScope: "local" });

const outline = computed(() => props.artifact.outline ?? []);
const isEmpty = computed(() => outline.value.length === 0);

/** Indent each block by its tree depth for a readable structural preview. */
function indentStyle(depth: number): Record<string, string> {
  const safe = Number.isFinite(depth) && depth > 0 ? depth : 0;
  return { paddingInlineStart: `${safe * 0.875}rem` };
}
</script>

<template>
  <div data-slot="chat-resource-preview" class="flex h-full flex-col gap-3 overflow-y-auto">
    <header class="flex items-center justify-between gap-2">
      <div class="min-w-0">
        <p class="truncate text-sm font-medium">
          {{ artifact.path || t("newResource") }}
        </p>
        <p v-if="artifact.baseVersion" class="truncate text-xs text-muted-foreground">
          {{ t("baseVersion", { version: artifact.baseVersion }) }}
        </p>
      </div>
      <Badge :variant="artifact.saved ? 'default' : 'secondary'">
        {{ artifact.saved ? t("saved") : t("draft") }}
      </Badge>
    </header>

    <p v-if="isEmpty" class="text-sm text-muted-foreground">
      {{ t("empty") }}
    </p>

    <ul v-else class="flex flex-col gap-1">
      <li
        v-for="(node, index) in outline"
        :key="node.id ?? `node-${index}`"
        class="flex items-center gap-2 rounded-md px-2 py-1 text-sm"
        :class="compact ? 'bg-muted/40' : 'hover:bg-muted/50'"
        :style="indentStyle(node.depth)"
      >
        <Badge v-if="node.componentId" variant="outline" class="shrink-0 font-mono text-[0.65rem]">
          {{ node.componentId }}
        </Badge>
        <span class="min-w-0 flex-1 truncate">
          {{ node.heading || node.id || t("untitledBlock") }}
        </span>
      </li>
    </ul>
  </div>
</template>

<i18n lang="yaml">
en:
  newResource: New resource
  baseVersion: Base version {version}
  saved: Saved
  draft: Draft
  empty: The draft is empty. As the assistant edits the resource, its structure will appear here.
  untitledBlock: Untitled block
</i18n>
